import React, { useLayoutEffect, useRef } from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { mergeStyleSets, keyframes } from '@fluentui/react/lib/Styling';

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const classes = mergeStyleSets({
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10,
    // ook zichtbaar als het paneel tijdens het laden nog leeg is
    minHeight: '60vh',
    background: 'rgba(255, 255, 255, 0.82)',
    // pas na 150 ms zichtbaar: een snelle lading geeft geen flits
    opacity: 0,
    animationName: fadeIn,
    animationDuration: '0.2s',
    animationDelay: '0.15s',
    animationFillMode: 'forwards',
  },
  spinner: {
    // blijft in beeld, ook in een lang paneel
    position: 'sticky',
    top: 120,
    paddingTop: 120,
  },
});

interface IPanelLoadingOverlay {
  /** true zolang het record van het paneel nog geladen wordt */
  show: boolean;
  label?: string;
}

/**
 * Laagje over de inhoud van een paneel terwijl het record nog geladen wordt: een spinner en
 * geen invoer mogelijk, zodat niemand begint te typen in een nog leeg formulier.
 * Plaats het als eerste kind van een Fluent `<Panel>`: het zoekt zelf de paneelinhoud
 * (`.ms-Panel-content`) op en positioneert die relatief. Kop en sluitknop vallen erbuiten,
 * dus het paneel blijft altijd te sluiten.
 */
const PanelLoadingOverlay = ({ show, label = 'Gegevens laden…' }: IPanelLoadingOverlay) => {
  const anchor = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const host = anchor.current?.closest('.ms-Panel-content') as HTMLElement | null;
    if (host && getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }
  }, []);

  return (
    <>
      <span ref={anchor} style={{ display: 'none' }} />
      {show && (
        <div className={classes.overlay} role='status' aria-live='polite'>
          <div className={classes.spinner}>
            <Spinner size={SpinnerSize.large} label={label} />
          </div>
        </div>
      )}
    </>
  );
};

export default PanelLoadingOverlay;
